import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MessageCircle,
  Calendar,
  Linkedin,
  Twitter,
  Instagram,
  Github,
  Youtube,
} from "lucide-react";
import { services } from "../data/services";
import { SITE } from "../data/site";
import { Logo } from "./Logo";

const socialIcons = [
  { icon: Linkedin, href: SITE.socials.linkedin, label: "LinkedIn" },
  { icon: Youtube, href: SITE.socials.youtube, label: "YouTube" },
  { icon: Instagram, href: SITE.socials.instagram, label: "Instagram" },
  // { icon: Github, href: SITE.socials.github, label: "GitHub" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-deep relative overflow-hidden">
    <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[700px] -translate-x-1/2 rounded-full bg-[#1C7543]/20 blur-[140px]" />
    <div className="relative mx-auto max-w-7xl px-6 pb-28 pt-20 md:px-12 md:pb-10">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <Link to="/" data-testid="footer-logo" aria-label="Social Wavez Technologies — home">
            <Logo size={40} />
          </Link>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/55">
            We build the systems your business runs on — websites, SaaS, CRM,
            AI automation and SEO, connected from day one.
          </p>
          <div className="mt-6 flex gap-3">
            {socialIcons.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all duration-300 hover:border-transparent hover:bg-gradient-to-r hover:from-[#1C7543] hover:to-[#D3B74D] hover:text-white"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Services">
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40">
            Services
          </h3>
          <ul className="mt-5 space-y-3">
            {services.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/services#${s.id}`}
                  data-testid={`footer-service-${s.id}`}
                  className="text-sm text-white/60 transition-colors duration-200 hover:text-white"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Company">
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40">
            Company
          </h3>
          <ul className="mt-5 space-y-3">
            {[
              { label: "About", to: "/#about" },
              { label: "Project", to: "/project" },
              { label: "Career", to: "/career" },
              { label: "Contact", to: "/contact" },
            ].map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-company-${l.label.toLowerCase()}`}
                  className="text-sm text-white/60 transition-colors duration-200 hover:text-white"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40">
            Get In Touch
          </h3>
          <ul className="mt-5 space-y-3 text-sm text-white/60">
            <li>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="footer-email"
                className="flex items-center gap-2.5 transition-colors duration-200 hover:text-white"
              >
                <Mail className="h-4 w-4 text-[#D3B74D]" /> {SITE.email}
              </a>
            </li>
            <li>
              <a
                href={`tel:${SITE.phone.replace(/\s/g, "")}`}
                data-testid="footer-phone"
                className="flex items-center gap-2.5 transition-colors duration-200 hover:text-white"
              >
                <Phone className="h-4 w-4 text-[#D3B74D]" /> {SITE.phone}
              </a>
            </li>
            <li>
              <a
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-2.5 transition-colors duration-200 hover:text-white"
              >
                <MessageCircle className="h-4 w-4 text-[#D3B74D]" /> WhatsApp us
              </a>
            </li>
            <li className="pt-2">
              <Link
                to="/contact"
                data-testid="footer-book-call"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1C7543] to-[#D3B74D] px-5 py-2.5 text-xs font-semibold text-white transition-shadow duration-300 hover:shadow-[0_8px_30px_rgba(211,183,77,0.5)]"
              >
                <Calendar className="h-3.5 w-3.5" /> Book a Free Discovery Call
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/40 md:flex-row">
        <p>© {new Date().getFullYear()} Social Wavez Technologies. All rights reserved.</p>
        <div className="flex gap-6">
          <a href="#privacy" data-testid="footer-privacy" className="transition-colors hover:text-white">
            Privacy Policy
          </a>
          <a href="#terms" data-testid="footer-terms" className="transition-colors hover:text-white">
            Terms
          </a>
        </div>
      </div>
    </div>
  </footer>
);
